/**
 * Issue #228 — 通话 PII 揭示 API 客户端（真实接口，强制 response.ok + content-type）。
 *
 * 揭示完整手机号 / 客户名需二次验证：携带有效 grace 凭证免密；否则提交当前管理员
 * 登录密码由后端 bcrypt 校验。成功后端续签 30 分钟 grace 凭证。
 */
import { apiClient } from './api';
import type { CallPiiReveal } from './types';
import { getValidGraceToken, rememberGrace } from './phone-reveal-grace';
import type { DeleteCaptchaProof } from './captcha-api';

export interface RevealCallPiiBody {
  reason: string;
  password?: string;
  graceToken?: string;
}

/** 直接调用揭示接口（POST /calls/:id/reveal），返回明文 + 续签的 grace 凭证。 */
export async function revealCallPiiApi(
  callId: string,
  body: RevealCallPiiBody,
): Promise<CallPiiReveal> {
  const data = await apiClient.post<CallPiiReveal>(`/calls/${callId}/reveal`, body);
  rememberGrace(data.graceToken, data.graceExpiresAt);
  return data;
}

/**
 * 高层封装：优先用会话内有效 grace 凭证（免密）；无有效凭证时必须由调用方提供
 * 密码（password）。两者都没有时抛错（UI 应先弹密码框）。
 */
export async function revealCallPhone(
  callId: string,
  reason: string,
  password?: string,
): Promise<CallPiiReveal> {
  const grace = getValidGraceToken();
  if (grace) {
    return revealCallPiiApi(callId, { reason, graceToken: grace });
  }
  if (!password) {
    throw new Error('需要输入当前管理员登录密码');
  }
  return revealCallPiiApi(callId, { reason, password });
}

/**
 * Issue #8 — 软删除通话记录（真实后端接口，仅 ADMIN / SUPER_ADMIN）。
 *
 * 高危操作：必须携带一次性图形验证码（captchaId + captchaCode，随 DELETE body 回传，
 * 由后端 consume 校验），与员工 / 课程 / 知识库删除同一模式。后端只写 deletedAt，
 * 录音与质检报告一律保留；删除后该记录从列表 / 详情 / 统计 / 导出中不可见。
 *
 * 红线：绝不允许前端只改本地 state 造出「已删除」的假象。
 */
export function deleteCallApi(
  callId: string,
  proof: DeleteCaptchaProof,
): Promise<{ id: string; deleted: boolean }> {
  return apiClient.delete<{ id: string; deleted: boolean }>(
    `/calls/${encodeURIComponent(callId)}`,
    proof,
  );
}
